import React from 'react';
import { motion } from 'framer-motion';
import { Radio } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { RadioStation } from '@/types';
import { getLocalizedStationName } from '@/utils/stationName';
import { getStreamColor, isGradient } from '@/utils/streamColor';

interface StationCardProps {
  station: RadioStation;
  isActive: boolean;
  /** Prva (LIVE) kartica zadržava postojeći stil sa gradient pozadinom */
  isLive?: boolean;
  onClick: () => void;
  index?: number;
}

const StationCard: React.FC<StationCardProps> = ({
  station,
  isActive,
  isLive = false,
  onClick,
  index = 0,
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'bs';

  const bgColor = getStreamColor(station.color, station.name);
  const localizedName = getLocalizedStationName(station, locale);
  const hasArtwork = Boolean(station.artwork && station.artwork.startsWith('http'));

  // === LIVE kartica — postojeći stil (gradient pozadina + slika + naslov ispod) ===
  if (isLive) {
    return (
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 + index * 0.05 }}
        onClick={onClick}
        className={`flex-shrink-0 rounded-xl p-3 w-44 min-w-[176px] overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
          isActive ? 'shadow-2xl scale-[1.02]' : ''
        }`}
        style={{
          background: isGradient(bgColor)
            ? bgColor
            : `linear-gradient(135deg, ${bgColor} 0%, ${bgColor} 100%)`,
        }}
      >
        <div>
          {hasArtwork ? (
            <img
              src={station.artwork}
              alt={localizedName}
              className="w-full aspect-video rounded-lg object-cover mb-2"
            />
          ) : (
            <div className="w-full aspect-video rounded-lg bg-white/30 flex items-center justify-center mb-2 backdrop-blur-sm">
              <Radio className="w-12 h-12 text-white drop-shadow" />
            </div>
          )}
        </div>
        <p className="text-sm font-bold text-white truncate uppercase tracking-wide drop-shadow-sm">
          {localizedName}
        </p>
      </motion.button>
    );
  }

  // === NOVI STIL — slika full-bleed na vrhu, naslov dole sa overlay-om, deblja linija ===
  const solidColor = isGradient(bgColor) ? '#a855f7' : bgColor;

  return (
    <motion.button
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.05 }}
      onClick={onClick}
      className={`relative flex-shrink-0 rounded-xl w-44 min-w-[176px] overflow-hidden transition-all duration-300 hover:scale-[1.03] ${
        isActive ? 'scale-[1.03]' : ''
      }`}
      style={{
        background: isGradient(bgColor)
          ? bgColor
          : `linear-gradient(180deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
        border: `3px solid ${solidColor}`,
        boxShadow: isActive
          ? `0 0 24px ${solidColor}aa, 0 8px 16px rgba(0,0,0,0.5)`
          : `0 4px 14px ${solidColor}66, 0 2px 6px rgba(0,0,0,0.3)`,
      }}
    >
      <div className="relative w-full aspect-video overflow-hidden">
        {hasArtwork ? (
          <img
            src={station.artwork}
            alt={localizedName}
            className="w-full h-full object-cover block"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: isGradient(bgColor)
                ? bgColor
                : `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}cc 100%)`,
            }}
          >
            <Radio className="w-12 h-12 text-white drop-shadow" />
          </div>
        )}
      </div>

      <div
        className="px-3 py-2 relative"
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, ${solidColor}ee 100%)`,
        }}
      >
        <p className="text-sm font-bold text-white truncate uppercase tracking-wide drop-shadow-lg">
          {localizedName}
        </p>
      </div>
    </motion.button>
  );
};

export default StationCard;
